import { useState, useMemo } from "react";
import type { PlanEvent } from "../../../shared/types";
import { useCategories, getCategoryBarColor } from "../../../shared/stores/category-store";
import {
  getWeekDays,
  getToday,
  addDays,
  getDayNumber,
  getMonthYear,
} from "../../../shared/types/date-helpers";
import { expandRecurringEvents } from "../../events/utils/expand-recurring";
import { timeToMinutes } from "../../calendar/utils/time-positioning";
import { WeeklyStatsTable } from "./stats/WeeklyStatsTable";
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from "../../../ui/icons";

interface WeeklyViewProps {
  readonly events: PlanEvent[];
}

function formatHours(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

export function WeeklyView({ events }: WeeklyViewProps) {
  const { categories, labels, categoryOptions } = useCategories();
  const [weekReference, setWeekReference] = useState<string>(getToday());

  const weekDays = useMemo(() => getWeekDays(weekReference), [weekReference]);

  const expanded = useMemo(() => {
    const weekStart = weekDays[0]!;
    const weekEnd = weekDays[6]!;
    return expandRecurringEvents(events, weekStart, weekEnd);
  }, [events, weekDays]);

  const categoryTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const cat of categories) {
      totals[cat] = 0;
    }
    for (const event of expanded) {
      if (event.allDay) continue;
      const duration =
        timeToMinutes(event.endTime) - timeToMinutes(event.startTime);
      if (duration > 0 && totals[event.category] !== undefined) {
        totals[event.category] += duration;
      }
    }
    return totals;
  }, [expanded, categories]);

  const dayByCat = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    for (const day of weekDays) {
      const dayRecord: Record<string, number> = {};
      for (const cat of categories) {
        dayRecord[cat] = 0;
      }
      map.set(day, dayRecord);
    }
    for (const event of expanded) {
      if (event.allDay) continue;
      const duration =
        timeToMinutes(event.endTime) - timeToMinutes(event.startTime);
      if (duration <= 0) continue;
      const entry = map.get(event.date);
      if (entry)
        entry[event.category] = (entry[event.category] ?? 0) + duration;
    }
    return map;
  }, [expanded, weekDays, categories]);

  const goalsRows = useMemo(() => {
    return categories
      .filter((cat) => categoryOptions[cat]?.goalHours)
      .map((cat) => {
        const goal = categoryOptions[cat].goalHours!;
        const minutes = categoryTotals[cat] ?? 0;
        const pct = Math.min(Math.round((minutes / (goal * 60)) * 100), 100);
        return { cat, label: labels[cat], minutes, goal, pct, color: getCategoryBarColor(cat) };
      });
  }, [categories, categoryOptions, categoryTotals, labels]);

  const firstDay = weekDays[0]!;
  const lastDay = weekDays[6]!;

  // Calculer le total hebdomadaire pour les barres de progression
  const weekTotalMinutes = useMemo(() => {
    return Object.values(categoryTotals).reduce((acc, val) => acc + val, 0);
  }, [categoryTotals]);

  return (
    <div className="flex flex-col gap-6 py-10">
      {/* Header */}
      <div className="flex items-center justify-between -mx-2 px-2 ">
        <button
          type="button"
          onClick={() => setWeekReference((ref) => addDays(ref, -7))}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-alt text-ink-muted transition-colors"
          aria-label="Semaine précédente"
        >
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
        <div className="text-center">
          <div className="text-sm font-bold text-ink">
            Semaine en résumé
          </div>
          <div className="text-xs text-ink-muted">
            {getDayNumber(firstDay)} {getMonthYear(firstDay).split(" ")[0]} –{" "}
            {getDayNumber(lastDay)} {getMonthYear(lastDay)}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setWeekReference((ref) => addDays(ref, 7))}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-alt text-ink-muted transition-colors"
          aria-label="Semaine suivante"
        >
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Séparateur visuel subtil */}
      <div className="h-px bg-border/50" />
      {/* Cartes de totaux avec barres de progression internes */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {categories.map((cat) => {
          const minutes = categoryTotals[cat] ?? 0;
          const pct = weekTotalMinutes > 0 ? (minutes / weekTotalMinutes) * 100 : 0;
          const barHeight = Math.max(pct, 4); // Min 4%
          const color = getCategoryBarColor(cat);
          return (
            <div
              key={cat}
              className="relative p-4 bg-surface border border-border rounded-xl text-center overflow-hidden group hover:border-accent/50 transition-colors"
            >
              {/* Barre de progression verticale subtile en fond */}
              <div
                className={`absolute bottom-0 left-0 right-0 transition-all duration-500 ${color}`}
                style={{ height: `${barHeight}%`, opacity: 0.15 }}
              />
              {/* Contenu au-dessus de la barre */}
              <div className="relative flex flex-col items-center justify-center min-h-[4rem]">
                <div className="text-2xl font-black text-ink">
                  {formatHours(minutes)}
                </div>
                <div className="text-[10px] text-ink-muted mt-1 uppercase tracking-wide leading-tight">
                  {labels[cat]}
                </div>
                {/* Indicateur de pourcentage du total — espace réservé */}
                <div className="text-[10px] mt-1 h-4">
                  {weekTotalMinutes > 0 && minutes > 0 ? (
                    <span className="text-ink-faint">
                      {Math.round((minutes / weekTotalMinutes) * 100)}%
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Section objectifs avec design amélioré */}
      {goalsRows.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="text-xs font-semibold text-ink-muted uppercase tracking-wider">
            Objectifs hebdomadaires
          </div>
          {goalsRows.map(({ cat, label, minutes, goal, pct, color }) => {
            const isGoalReached = pct >= 100;
            return (
              <div
                key={cat}
                className="p-4 bg-surface border rounded-xl transition-all"
                style={isGoalReached ? {
                  borderColor: "rgba(22, 163, 74, 0.3)",
                  backgroundColor: "rgba(22, 163, 74, 0.05)"
                } : {
                  borderColor: "var(--color-border)"
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-ink">
                      {label}
                    </span>
                    {isGoalReached && (
                      <span className="flex items-center" style={{ color: "rgb(22, 163, 74)" }}>
                        <CheckIcon className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium" style={isGoalReached ? { color: "rgb(22, 163, 74)" } : {}}>
                    {formatHours(minutes)} / {goal}h
                    <span className="text-xs ml-1 opacity-60">({pct}%)</span>
                  </span>
                </div>
                <div className="w-full h-3 bg-surface-alt rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${isGoalReached ? "" : color}`}
                    style={{
                      width: `${Math.min(pct, 100)}%`,
                      backgroundColor: isGoalReached ? "rgb(22, 163, 74)" : undefined
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tableau des stats avec améliorations */}
      <WeeklyStatsTable weekDays={weekDays} dayByCat={dayByCat} categoryTotals={categoryTotals} />
    </div>
  );
}
